// [context-picker] Pick a certified template or a Workspace draft to send with
// the next chat turn.
//
// Why it exists: the composer could only attach FILES. A draft already in the
// Workspace — the deal memo you want expanded, the agreement you want reviewed —
// could only reach the agent if you remembered its exact title and typed it, and
// the agent then guessed which one you meant. Live, that failed on the first try:
// asked to expand "PERFORMER DEAL OFFER MEMO", the agent answered that it
// couldn't find a draft by that name.
//
// A pick is an instruction, so it stages like an attachment (a chip you can
// remove) and is sent with the turn, rather than persisting on the session the
// way the side rail's activeRecord does.
//
// [insert-pickers wireframe] Two-pane shell from the "Insert Pickers" design:
// search + filter chips + grouped list on the left, a detail panel on the right
// so you can tell two similarly-named documents apart before committing. The
// wireframe's own note said its metadata was a guess made without the codebase,
// so the facts grid here shows only fields that actually exist on Draft and
// CertifiedTemplateRow — no invented jurisdiction, page count or usage trail.
import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "../../components/Dialog";
import { Button } from "../../components/Button";
import { useDrafts } from "../../data/draftsQuery";
import {
  useCertifiedTemplates,
  useTenantCertifiedTemplates,
} from "../../data/templatesQuery";
import { useSession } from "../../auth/AuthProvider";

export type PickedRecord = { id: string; title: string };

export type ContextPickerKind = "template" | "draft";

export type ContextPickerProps = {
  kind: ContextPickerKind | null;
  onClose: () => void;
  onSelect: (kind: ContextPickerKind, record: PickedRecord) => void;
  /** [S-769] Open with a facet already selected — "Deal memo" when the agent
   *  asked which SHORT form to expand, so the user starts on the list the next
   *  step can actually accept. They can still clear it. */
  initialFilter?: string;
};

/** One fact in the detail panel's grid. Rendered only when it has a value, so a
 *  record missing a field shows a shorter grid rather than an empty cell. */
type Fact = { label: string; value: string };

type Row = {
  id: string;
  title: string;
  /** Short tags under the title — deal type, guild, lifecycle. */
  tags: string[];
  /** The dim second line: whatever most helps tell two similar rows apart. */
  subtitle: string;
  /** Section this row sits under. */
  group: string;
  /** Certified/re-certified templates get the green status pill. */
  certified?: boolean;
  facts: Fact[];
  /** Every facet this row can be filtered by — deal type AND document form.
   *  An array because a row legitimately belongs to more than one chip:
   *  "Deal memo" and "Feature film" are both true of the same draft, and
   *  filtering to short forms is the scan that matters when you are choosing
   *  something to EXPAND. */
  filter: string[];
};

const SENTENCE_CASE: Record<string, string> = {
  feature_film: "Feature film",
  tv_series: "TV series",
  streaming: "Streaming",
  documentary: "Documentary",
  commercial: "Commercial",
};

function label(value: string | null | undefined): string {
  if (value === null || value === undefined || value.length === 0) return "";
  return SENTENCE_CASE[value] ?? value.replace(/_/g, " ");
}

/** [context-picker] Drafts record their form only when generated from a
 *  certified template; a pasted or pre-existing draft has none, and guessing
 *  one from byte count would be inventing metadata. Blank is honest. */
function formLabel(form: string | null | undefined): string {
  if (form === "long_form") return "Long form";
  if (form === "deal_memo") return "Deal memo";
  return "";
}

function shortDate(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso.length === 0) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Title fallback mirrors the certified library's own: legacy rows carry no
 *  title, so derive a readable one instead of showing a bare id. */
function templateLabel(t: {
  id: string;
  title?: string | null;
  dealType?: string;
  guild?: string;
}): string {
  if (typeof t.title === "string" && t.title.trim().length > 0) return t.title;
  const bits = [label(t.dealType), t.guild ?? ""].filter((b) => b.length > 0);
  return bits.length > 0 ? `Certified standard — ${bits.join(" · ")}` : t.id;
}

export function ContextPicker({
  kind,
  onClose,
  onSelect,
  initialFilter,
}: ContextPickerProps): JSX.Element | null {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(initialFilter ?? "all");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Both hooks always run (rules of hooks); only the open one's data is read.
  const drafts = useDrafts();
  // [layer] Certified templates come in TWO layers and the picker needs both.
  // Driving this live, the dialog said "No certified templates available" on a
  // tenant whose library is full — because useCertifiedTemplates alone hits
  // /layer1/templates, and this tenant's certified templates are its own L2.
  const session = useSession();
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";
  const l1 = useCertifiedTemplates({});
  const l2 = useTenantCertifiedTemplates(tenantId, {});

  const allRows = useMemo<Row[]>(() => {
    if (kind === "draft") {
      const list = [...(drafts.data ?? [])].sort((a, b) =>
        (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "")
      );
      return list.map((d, i) => ({
        id: d.id,
        title: d.title,
        // Form first: it is the thing people scan for ("which one is the
        // long form?"), and searching "long" found nothing before drafts
        // recorded it.
        tags: [formLabel(d.documentForm), label(d.dealType), d.guild].filter(
          (t) => t.length > 0
        ),
        subtitle: [
          shortDate(d.updatedAt),
          d.currentVersion > 0 ? `v${d.currentVersion}` : "",
          d.sharedWith.length > 0 ? `shared with ${d.sharedWith.length}` : "",
        ]
          .filter((s) => s.length > 0)
          .join(" · "),
        // Most recent first, so the one you were just working on is at the top
        // under its own heading rather than lost in a long alphabetical list.
        group: i < 3 ? "Recently updated" : "All drafts",
        facts: [
          { label: "Form", value: formLabel(d.documentForm) },
          { label: "Version", value: d.currentVersion > 0 ? `v${d.currentVersion}` : "—" },
          { label: "Last updated", value: shortDate(d.updatedAt) },
          { label: "Deal type", value: label(d.dealType) },
          { label: "Guild", value: d.guild },
          {
            label: "Length",
            value:
              d.contentLength > 0
                ? `${Math.round(d.contentLength / 1000)}k characters`
                : "Empty",
          },
          {
            label: "Shared with",
            value: d.sharedWith.length > 0 ? `${d.sharedWith.length} people` : "Just you",
          },
        ].filter((f) => f.value.length > 0),
        // Form first so the short-form chip leads: picking a document to expand
        // is the case where "which one is the deal memo?" is the whole question.
        filter: [formLabel(d.documentForm), label(d.dealType)].filter((x) => x.length > 0),
      }));
    }
    // Tenant templates first, then L1, de-duplicated by id — the same
    // precedence the certified library screen uses.
    const merged = [...(l2.data ?? []), ...(l1.data ?? [])].filter(
      (t, i, arr) => arr.findIndex((x) => x.id === t.id) === i
    );
    return merged.map((t) => {
      const certified =
        t.lifecycleState === "certified" || t.lifecycleState === "re_certified";
      return {
        id: t.id,
        title: templateLabel(t),
        tags: [label(t.dealType), t.guild].filter((x) => x.length > 0),
        subtitle: [
          t.layerSource === "L2" ? "Your studio's library" : "Platform standard",
          t.tier !== null && t.tier !== undefined ? String(t.tier) : "",
        ]
          .filter((s) => s.length > 0)
          .join(" · "),
        group: t.layerSource === "L2" ? "Your studio" : "Platform library",
        certified,
        facts: [
          { label: "Deal type", value: label(t.dealType) },
          { label: "Guild", value: t.guild ?? "" },
          { label: "Tier", value: t.tier !== null && t.tier !== undefined ? String(t.tier) : "" },
          { label: "Layer", value: t.layerSource === "L2" ? "Studio (L2)" : "Platform (L1)" },
          { label: "Form", value: label(t.documentForm) },
          {
            label: "Status",
            value: label(t.lifecycleState),
          },
        ].filter((f) => f.value.length > 0),
        filter: [label(t.documentForm), label(t.dealType)].filter((x) => x.length > 0),
      };
    });
  }, [kind, drafts.data, l1.data, l2.data]);

  /** Deal types actually present — the wireframe's Talent/Crew/Production chips
   *  are not a taxonomy this product has, so the chips are built from the data. */
  const chips = useMemo(() => {
    const seen: string[] = [];
    for (const r of allRows) {
      for (const f of r.filter) if (f.length > 0 && !seen.includes(f)) seen.push(f);
    }
    return seen;
  }, [allRows]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      if (filter !== "all" && !r.filter.includes(filter)) return false;
      if (q.length === 0) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [allRows, query, filter]);

  // Keep the highlight in range as the list narrows, and keep it visible.
  useEffect(() => {
    setActive((a) => (a >= rows.length ? 0 : a));
  }, [rows.length]);
  useEffect(() => {
    listRef.current
      ?.querySelector('[data-active="true"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (kind === null) return null;

  const loading = kind === "draft" ? drafts.isPending : l1.isPending || l2.isPending;
  const error = kind === "draft" ? drafts.error : (l1.error ?? l2.error);
  const isTemplate = kind === "template";
  const current = rows[active];

  const commit = (row: Row): void => {
    onSelect(kind, { id: row.id, title: row.title });
    setQuery("");
    setFilter("all");
    onClose();
  };

  // ↑↓ move the highlight, Enter commits — all without leaving the search field.
  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && current !== undefined) {
      e.preventDefault();
      commit(current);
    }
  };

  let lastGroup = "";

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) {
          setQuery("");
          setFilter("all");
          onClose();
        }
      }}
      contentClassName="ds-dialog__content sa-picker__dialog"
      titleClassName="sa-picker__title"
      descriptionClassName="sa-picker__subtitle"
      title={
        <>
          <span className="sa-picker__eyebrow">
            {isTemplate ? "Certified library" : "Draft workspace"}
          </span>
          {isTemplate ? "Attach a template to this message" : "Attach a draft to this message"}
        </>
      }
      description={
        isTemplate
          ? "The agent drafts from the template you pick instead of choosing one itself."
          : "The agent works from the draft you pick — expanding, reviewing or revising it."
      }
    >
      <div className="sa-picker">
        <div className="sa-picker__panes">
          <div className="sa-picker__list-pane">
            <input
              className="sa-picker__search"
              type="text"
              autoFocus
              value={query}
              placeholder={
                isTemplate ? "Search templates by name or type…" : "Search drafts by name or type…"
              }
              aria-label={isTemplate ? "Search templates" : "Search drafts"}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onSearchKey}
            />

            {chips.length > 1 && (
              <div className="sa-picker__chips" role="group" aria-label="Filter by document form or deal type">
                <Button
                  variant="bare"
                  className={`sa-picker__chip${filter === "all" ? " sa-picker__chip--on" : ""}`}
                  aria-pressed={filter === "all"}
                  onClick={() => {
                    setFilter("all");
                    setActive(0);
                  }}
                >
                  All
                </Button>
                {chips.map((c) => (
                  <Button
                    key={c}
                    variant="bare"
                    className={`sa-picker__chip${filter === c ? " sa-picker__chip--on" : ""}`}
                    aria-pressed={filter === c}
                    onClick={() => {
                      setFilter(c);
                      setActive(0);
                    }}
                  >
                    {label(c)}
                  </Button>
                ))}
              </div>
            )}

            {loading && <p className="sa-picker__msg">Loading…</p>}
            {error !== null && (
              <p className="sa-picker__msg" role="alert">
                Couldn&apos;t load {isTemplate ? "templates" : "drafts"}: {error.message}
              </p>
            )}
            {!loading && error === null && rows.length === 0 && (
              <p className="sa-picker__msg">
                {query.trim().length > 0 || filter !== "all"
                  ? `No ${isTemplate ? "certified template" : "draft"} matches that.`
                  : isTemplate
                    ? "No certified templates available."
                    : "You have no drafts yet."}
              </p>
            )}

            <ul className="sa-picker__list" ref={listRef}>
              {rows.map((r, i) => {
                const header = r.group !== lastGroup ? r.group : null;
                lastGroup = r.group;
                return (
                  <li key={r.id}>
                    {header !== null && <p className="sa-picker__group">{header}</p>}
                    <Button
                      variant="bare"
                      className={`sa-picker__row${i === active ? " sa-picker__row--on" : ""}`}
                      data-active={i === active}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => commit(r)}
                    >
                      <span className="sa-picker__row-title">{r.title}</span>
                      {r.tags.length > 0 && (
                        <span className="sa-picker__row-tags">{r.tags.join(" · ")}</span>
                      )}
                      {r.subtitle.length > 0 && (
                        <span className="sa-picker__row-sub">{r.subtitle}</span>
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="sa-picker__detail">
            {current === undefined ? (
              <p className="sa-picker__msg">
                {rows.length === 0
                  ? "Nothing to preview."
                  : "Select a row to see its details."}
              </p>
            ) : (
              <>
                <h3 className="sa-picker__detail-title">{current.title}</h3>
                <div className="sa-picker__pills">
                  {current.certified === true && (
                    <span className="sa-picker__pill sa-picker__pill--certified">Certified</span>
                  )}
                  {current.tags.map((t) => (
                    <span key={t} className="sa-picker__pill">
                      {t}
                    </span>
                  ))}
                </div>
                <dl className="sa-picker__facts">
                  {current.facts.map((f) => (
                    <div key={f.label} className="sa-picker__fact">
                      <dt className="sa-picker__fact-label">{f.label}</dt>
                      <dd className="sa-picker__fact-value">{f.value}</dd>
                    </div>
                  ))}
                </dl>
                <Button
                  variant="primary"
                  className="sa-picker__commit"
                  onClick={() => commit(current)}
                >
                  {isTemplate ? "Attach template" : "Attach draft"}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
